import { useEffect, useState } from "react";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from "recharts";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Data
import { Box, Grid, Typography } from "@mui/material";
import { SignupService } from "service/signup/service";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DefaultStatisticsCard from "examples/Cards/StatisticsCards/DefaultStatisticsCard";
import { stat } from "fs";
import PieChart from "examples/Charts/PieChart";

const dummyData = {
  visit: {
    day: { count: 100, percent: "+1.3%" },
    week: { count: 1000, percent: "+1.3%" },
    month: { count: 10000, percent: "+1.3%" },
    all: { count: 10000, percent: " " },
  },
  pv: {
    day: { count: 100, percent: "+1.3%" },
    week: { count: 1000, percent: "+1.3%" },
    month: { count: 10000, percent: "+1.3%" },
    all: { count: 10000, percent: "" },
  },
  new: {
    day: { count: 100, percent: "+1.3%" },
    week: { count: 1000, percent: "+1.3%" },
    month: { count: 10000, percent: "+1.3%" },
    all: { count: 10000, percent: "" },
  },
};

interface Stat {
  today: string;
  thisWeek: string;
  thisMonth: string;
  total: string;
  todayRate: string;
  thisWeekRate: string;
  thisMonthRate: string;
}

interface DeviceData {
  desktop: number;
  mobile: number;
  tablet: number;
}

function Dashboard(): JSX.Element {
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [stat, setStat] = useState<Stat>();
  const [pageViews, setPageViews] = useState<Stat>();
  const [traffic, setTraffic] = useState<Stat>();
  const [monthlyDeviceData, setMonthlyDeviceData] = useState<DeviceData>();
  const [weeklyDeviceData, setWeeklyDeviceData] = useState<DeviceData>();

  const todayDate = new Date().toLocaleDateString().replace(/\.$/, "");

  useEffect(() => {
    const getData = async () => {
      try {
        const data = await SignupService.getStatData();

        const { stat, traffic, pageViews } = data;
        const { daily_data, monthly_data } = data.trend;
        const { monthlyDeviceData, weeklyDeviceData } = data.deviceData;

        setStat(stat);
        setTraffic(traffic);
        setPageViews(pageViews);
        setDailyData(daily_data);
        setMonthlyData(monthly_data);
        setMonthlyDeviceData(monthlyDeviceData);
        setWeeklyDeviceData(weeklyDeviceData);
      } catch (error) {
        console.log(error);
      }
    };

    getData();
  }, []);

  const CustomLegend = (props: any) => {
    const { payload } = props;

    const mapValueToText = (value: string) => {
      switch (value) {
        case "kakao":
          return "카카오";
        case "naver":
          return "네이버";
        case "common":
          return "일반";
        case "facebook":
          return "페이스북";
        case "google":
          return "구글";
        case "total":
          return "";
        default:
          return value;
      }
    };

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          gap: "10px",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {payload.map((entry: any, index: any) => (
          <div key={`item-${index}`} style={{ display: "flex", alignItems: "center" }}>
            {entry.value !== "total" && (
              <div
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: entry.color,
                  borderRadius: "50%",
                  marginRight: 5,
                }}
              />
            )}
            <span style={{ fontSize: 13, fontWeight: 600, color: entry.color }}>
              {mapValueToText(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const titleLabel: { label: string; value: "visit" | "pv" | "new" }[] = [
    { label: "방문자 수(트래픽)", value: "visit" },
    { label: "페이지 뷰(PV)", value: "pv" },
    { label: "회원가입", value: "new" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox display="flex" width="100%">
        <MDTypography style={{ marginLeft: "auto", color: "#242424" }} fontWeight="medium">
          {todayDate}
        </MDTypography>
      </MDBox>

      <Grid container direction="column" mt={3}>
        <Grid container spacing={2} direction={window.innerWidth > 1440 ? "row" : "column"}>
          <Grid item xs={12} md={6}>
            <Grid container direction="column" spacing={2}>
              {titleLabel.map(
                (title: { label: string; value: "visit" | "pv" | "new" }, idx1: number) => {
                  let currentStat: Stat | undefined;
                  if (title.value === "visit") currentStat = traffic;
                  else if (title.value === "pv") currentStat = pageViews;
                  else if (title.value === "new") currentStat = stat;

                  const statItems = [
                    { label: "당일", value: currentStat?.today, rate: currentStat?.todayRate },
                    {
                      label: "주간",
                      value: currentStat?.thisWeek,
                      rate: currentStat?.thisWeekRate,
                    },
                    {
                      label: "월간",
                      value: currentStat?.thisMonth,
                      rate: currentStat?.thisMonthRate,
                    },
                    { label: "전체", value: currentStat?.total, rate: null },
                  ];
                  return (
                    <Grid item key={idx1}>
                      <Card sx={{ p: 2 }}>
                        <Typography fontWeight="bold" fontSize="16px">
                          {title.label}
                        </Typography>
                        <Grid container justifyContent="space-between" mt={1}>
                          {statItems.map((item, index) => (
                            <Grid key={index} item sx={{ textAlign: "center", minWidth: "100px" }}>
                              <Box display="flex" justifyContent="center" gap={3}>
                                <Typography sx={{ fontSize: "14px" }}>{item.label}</Typography>
                                {item.rate !== null && (
                                  <Typography sx={{ color: "blue", fontSize: "14px" }}>
                                    {item.rate}%
                                  </Typography>
                                )}
                              </Box>
                              <Typography fontWeight="bold" fontSize="14px">
                                {item.value?.toLocaleString()}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Card>
                    </Grid>
                  );
                }
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, height: "100%" }}>
              <Typography fontWeight="bold" fontSize="16px" sx={{ mb: 2 }}>
                디바이스 별 방문자
              </Typography>
              <Grid container direction="row" justifyContent="center" alignItems="stretch" gap={4}>
                <Grid item>
                  <Box height="100%" display="flex" flexDirection="column">
                    <PieChart
                      icon={{ component: "" }}
                      title="주간"
                      chart={{
                        labels: ["모바일", "데스크톱", "태블릿"],
                        datasets: {
                          label: "Projects",
                          backgroundColors: ["info", "success", "warning"],
                          data: [
                            weeklyDeviceData?.mobile,
                            weeklyDeviceData?.desktop,
                            weeklyDeviceData?.tablet,
                          ],
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item>
                  <Box height="100%" display="flex" flexDirection="column">
                    <PieChart
                      icon={{ component: "" }}
                      title="월간"
                      chart={{
                        labels: ["모바일", "데스크톱", "태블릿"],
                        datasets: {
                          label: "Projects",
                          backgroundColors: ["info", "success", "warning"],
                          data: [
                            monthlyDeviceData?.mobile,
                            monthlyDeviceData?.desktop,
                            monthlyDeviceData?.tablet,
                          ],
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
        {/* <div style={{ width: "100%", display: "flex", gap: "16px", flexWrap: "wrap" }}>
          {[
            { data: total, title: "총 가입자수" },
            { data: yesterday, title: "전일 가입자수" },
            { data: today, title: "오늘 가입자수" },
            { data: prevWeek, title: "전주 주간 누적 가입자수" },
            { data: past1M, title: "전월 누적 가입자수" },
          ].map((item, index) => {
            return (
              <div key={index} style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                <ComplexStatisticsCard
                  color="success"
                  icon="person"
                  title={item.title}
                  count={item.data?.total.toLocaleString()}
                  increase={
                    index === 0
                      ? "총 회원수: " + item.data?.currentTotal.toLocaleString() + "명"
                      : item.title === "전주 주간 누적 가입자수"
                      ? `전전주 대비 증가(${Number(item.data?.rate).toFixed(1)}%)`
                      : ""
                  }
                  percentage={{
                    label: (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        {[
                          // { count: "facebook", img: "facebook" },
                          { count: "common", img: "Logo_Symbol" },
                          {
                            count: "kakao",
                            img: "png-transparent-kakaotalk-macos-bigsur-icon-thumbnail",
                          },
                          { count: "naver", img: "unnamed" },
                          { count: "google", img: "google" },
                        ].map((el, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <img
                              src={`/assets/images/${el.img}.png`}
                              alt=""
                              style={{
                                width: "20px",
                                height: "20px",
                              }}
                            />
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#344767",
                              }}
                            >
                              {item.data &&
                                Number(
                                  item.data[el.count as keyof typeof item.data]
                                ).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ),
                  }}
                />
              </div>
            );
          })}
        </div> */}
        <Grid container item xs={12} spacing={3}>
          <Grid direction="column" item container xs={12}>
            <MDBox my={3}>
              <Card sx={{ p: 2 }}>
                <Typography fontWeight="bold" fontSize="16px" sx={{ mb: 2 }}>
                  일자별 가입자 수 추이
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart
                    data={dailyData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 20,
                    }}
                    barSize={80}
                  >
                    <CartesianGrid stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip contentStyle={{ fontSize: "16px" }} />
                    <Legend content={<CustomLegend />} />
                    <Bar dataKey="kakao" stackId="a" fill="#f9e000" />
                    <Bar dataKey="naver" stackId="a" fill="#2DB400" />
                    <Bar dataKey="common" stackId="a" fill="#1E9B6B" />
                    <Bar dataKey="google" stackId="a" fill="red" />
                    {/* <Bar dataKey="facebook" stackId="a" fill="#3B5998" /> */}
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="black"
                      strokeWidth={2}
                      dot={{ stroke: "black", strokeWidth: 2, r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            </MDBox>
            <MDBox my={3}>
              <Card sx={{ p: 2 }}>
                <Typography fontWeight="bold" fontSize="16px" sx={{ mb: 2 }}>
                  월별 가입자 수 추이
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart
                    data={monthlyData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 20,
                    }}
                    barSize={80}
                  >
                    <CartesianGrid stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip contentStyle={{ fontSize: "16px" }} />
                    <Legend content={<CustomLegend />} />
                    <Bar dataKey="kakao" stackId="a" fill="#f9e000" />
                    <Bar dataKey="naver" stackId="a" fill="#2DB400" />
                    <Bar dataKey="common" stackId="a" fill="#1E9B6B" />
                    {/* <Bar dataKey="facebook" stackId="a" fill="#3B5998" /> */}
                    <Bar dataKey="google" stackId="a" fill="red" />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="black"
                      strokeWidth={2}
                      dot={{ stroke: "black", strokeWidth: 2, r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
              {/* <Card>
                <GradientLineChart
                  icon={{ component: "show_chart" }}
                  title="월별 회원가입 추이"
                  description="카카오, 네이버, 일반"
                  chart={chartData2}
                  height={500}
                />
              </Card> */}
            </MDBox>
          </Grid>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
}

export default Dashboard;
